import { DebouncedSearchBar, PageBreadcrumbsWithLinks } from '@/components'
import DataGridWithPagination from '@/components/DataGrid/DataGridWithPagination'
import { KpiStatCard } from '@/components/dashboard'
import { PageFilter, PageFilterFields } from '@/components/PageFilter'
import { PageBody, PageWrapper } from '@/components/PageWrapper'
import { dashboardShellBreadcrumb } from '@/constants/adminPage'
import { MenuLinks } from '@/constants/menu'
import { PagingVariables } from '@/constants/paging'
import { gridActionsColumnDef, gridHelper, type GridSortingDetails } from '@/helpers/grid.helper'
import { messageHelper } from '@/helpers/message.helper'
import { AnimationSkeleton } from '@/pages/ui/Skeleton'
import { transactionAdminApi } from '@/services/transactionAdminApi'
import {
	SubscriptionPaymentStatusTypes,
	normalizePaymentKind,
	normalizePaymentStatus,
	type AdminTransactionListResponse,
	type PaginationResponse,
	type TransactionAnalyticsSummaryResponse,
	type TransactionDetailResponse,
} from '@/services/transactionTypes'
import { toApiDateRange } from '@/pages/dashboard/utils/dateRangeUtils'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TransactionDetailModal, TransactionViewButton } from './Components/TransactionDetailModal'

const toLocalYmd = (date: Date) => {
	const y = date.getFullYear()
	const m = String(date.getMonth() + 1).padStart(2, '0')
	const d = String(date.getDate()).padStart(2, '0')
	return `${y}-${m}-${d}`
}

const defaultDateRange = () => {
	const today = new Date()
	const yesterday = new Date(today)
	yesterday.setDate(yesterday.getDate() - 1)
	return { dateFrom: toLocalYmd(yesterday), dateTo: toLocalYmd(today) }
}

const statusLabel = (status: SubscriptionPaymentStatusTypes | string | number) => {
	switch (normalizePaymentStatus(status)) {
		case SubscriptionPaymentStatusTypes.Succeeded:
			return 'Succeeded'
		case SubscriptionPaymentStatusTypes.Failed:
			return 'Failed'
		case SubscriptionPaymentStatusTypes.Pending:
			return 'Pending'
		case SubscriptionPaymentStatusTypes.Refunded:
			return 'Refunded'
		default:
			return 'Unknown'
	}
}

const statusClass = (status: SubscriptionPaymentStatusTypes | string | number) => {
	switch (normalizePaymentStatus(status)) {
		case SubscriptionPaymentStatusTypes.Succeeded:
			return 'orbit-pill orbit-pill--success'
		case SubscriptionPaymentStatusTypes.Failed:
			return 'orbit-pill orbit-pill--danger'
		case SubscriptionPaymentStatusTypes.Pending:
			return 'orbit-pill orbit-pill--warning'
		default:
			return 'orbit-pill'
	}
}

const formatMoney = (amount: number, currency: string) => {
	try {
		return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(amount)
	} catch {
		return `${amount.toFixed(2)} ${currency.toUpperCase()}`
	}
}

const formatDate = (value: string) => {
	const d = new Date(value)
	return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
}

const ManageTransactions = () => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(true)
	const [rowData, setRowData] = useState<PaginationResponse<AdminTransactionListResponse>>()
	const [analytics, setAnalytics] = useState<TransactionAnalyticsSummaryResponse | null>(null)
	const [gridSortingDetails, setGridSortingDetails] = useState<GridSortingDetails>({ sortField: 'createdOn', sortOrder: 'desc' })
	const [selectedPageSize, setSelectedPageSize] = useState(PagingVariables.DefaultPageSize)
	const [searchText, setSearchText] = useState('')
	const [detail, setDetail] = useState<TransactionDetailResponse | null>(null)
	const [detailLoading, setDetailLoading] = useState(false)

	const openDetail = async (row: AdminTransactionListResponse) => {
		setDetail(null)
		setDetailLoading(true)
		try {
			const result = await transactionAdminApi.getById(row.id, normalizePaymentKind(row.kind))
			setDetail(result)
		} catch (e) {
			messageHelper.showError(String(e))
			setDetailLoading(false)
			return
		}
		setDetailLoading(false)
	}

	const [filterStatus, setFilterStatus] = useState<SubscriptionPaymentStatusTypes | ''>('')
	const [dateFrom, setDateFrom] = useState(() => defaultDateRange().dateFrom)
	const [dateTo, setDateTo] = useState(() => defaultDateRange().dateTo)

	const [appliedFilters, setAppliedFilters] = useState({
		status: '' as SubscriptionPaymentStatusTypes | '',
		...defaultDateRange(),
	})

	const handleSearchChange = useCallback((value: string) => setSearchText(value), [])

	const filterPayload = useMemo(
		() => ({
			sortField: gridSortingDetails.sortField || 'createdOn',
			sortOrder: gridSortingDetails.sortOrder || 'desc',
			paymentStatus: appliedFilters.status === '' ? undefined : appliedFilters.status,
			dateFrom: appliedFilters.dateFrom
				? toApiDateRange(appliedFilters.dateFrom, appliedFilters.dateFrom).dateFrom
				: undefined,
			dateTo: appliedFilters.dateTo ? toApiDateRange(appliedFilters.dateTo, appliedFilters.dateTo).dateTo : undefined,
			keyword: searchText.trim() || undefined,
		}),
		[appliedFilters, gridSortingDetails, searchText]
	)

	const fetchTransactions = async (pageNumber: number, pageSize = selectedPageSize) => {
		const list = await transactionAdminApi.search({
			pageNumber,
			pageSize,
			...filterPayload,
		})
		setRowData(list)
	}

	useEffect(() => {
		let cancelled = false
		const load = async () => {
			setLoading(true)
			try {
				const [list, summary] = await Promise.all([
					transactionAdminApi.search({
						pageNumber: 1,
						pageSize: selectedPageSize,
						...filterPayload,
					}),
					transactionAdminApi.getAnalyticsSummary({
						pageNumber: 1,
						pageSize: 1,
						...filterPayload,
					}),
				])
				if (cancelled) return
				setRowData(list)
				setAnalytics(summary)
			} catch (e) {
				if (!cancelled) messageHelper.showError(String(e))
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		void load()
		return () => {
			cancelled = true
		}
	}, [filterPayload])

	const onApplyFilters = () => {
		setAppliedFilters({
			status: filterStatus,
			dateFrom,
			dateTo,
		})
	}

	const onClearFilters = () => {
		const dates = defaultDateRange()
		setFilterStatus('')
		setDateFrom(dates.dateFrom)
		setDateTo(dates.dateTo)
		setSearchText('')
		setAppliedFilters({ status: '', ...dates })
	}

	const [columnDefs] = useState([
		{
			field: 'createdOn',
			headerName: 'Date',
			minWidth: 180,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			valueFormatter: (params: { value: string }) => formatDate(params.value),
		},
		{
			field: 'tenantName',
			headerName: 'Tenant',
			minWidth: 220,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			cellRenderer: (params: { data: AdminTransactionListResponse }) => (
				<div className="flex h-full min-w-0 flex-col justify-center leading-tight">
					{params.data.tenantId ? (
						<Link
							to={MenuLinks.CustomerDetail.replace(':tenantId', String(params.data.tenantId))}
							className="truncate font-medium text-[hsl(var(--primary))] hover:underline"
						>
							{params.data.tenantName}
						</Link>
					) : (
						<div className="truncate font-medium">{params.data.tenantName}</div>
					)}
					<div className="truncate text-xs text-[hsl(var(--muted-foreground))]">{params.data.tenantAdminEmail}</div>
				</div>
			),
		},
		{
			field: 'planName',
			headerName: 'Plan',
			minWidth: 160,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
		},
		{
			field: 'amount',
			headerName: 'Amount',
			minWidth: 130,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			valueFormatter: (params: { data: AdminTransactionListResponse }) => formatMoney(params.data.amount, params.data.currency),
		},
		{
			field: 'paymentStatus',
			headerName: 'Status',
			minWidth: 120,
			cellClass: 'cell-badge',
			headerClass: 'cell-badge',
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			cellRenderer: (params: { value: SubscriptionPaymentStatusTypes | string }) => (
				<span className={statusClass(params.value)}>{statusLabel(params.value)}</span>
			),
		},
		{
			field: 'stripePaymentIntentId',
			headerName: 'Stripe ID',
			minWidth: 180,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			cellRenderer: (params: { value?: string }) => <span className="font-mono text-xs">{params.value ?? '—'}</span>,
		},
		{
			field: 'invoiceUrl',
			headerName: 'Invoice',
			sortable: false,
			minWidth: 110,
			cellRenderer: (params: { value?: string }) =>
				params.value ? (
					<a href={params.value} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] hover:underline">
						View
					</a>
				) : (
					'—'
				),
		},
		{
			field: 'id',
			headerName: 'Actions',
			...gridActionsColumnDef,
			cellRenderer: (params: { data: AdminTransactionListResponse }) => (
				<TransactionViewButton onClick={() => void openDetail(params.data)} />
			),
		},
	] as any)

	return (
		<>
			<PageBreadcrumbsWithLinks
				title={t('Manage.Transactions.Heading', 'Transactions')}
				subNames={dashboardShellBreadcrumb(t('Manage.Transactions.Breadcrumb', 'Transactions'))}
			/>

			<PageWrapper>
				<PageBody>
					{analytics && (
						<div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
							<KpiStatCard
								label="Total"
								value={analytics.totalCount.toLocaleString()}
								iconClass="ri-exchange-line"
								accentClass="text-primary"
							/>
							<KpiStatCard
								label="Succeeded"
								value={analytics.succeededCount.toLocaleString()}
								iconClass="ri-checkbox-circle-line"
								accentClass="text-success"
								className="[&_p.font-display]:text-emerald-500"
							/>
							<KpiStatCard
								label="Failed"
								value={analytics.failedCount.toLocaleString()}
								iconClass="ri-close-circle-line"
								accentClass="text-destructive"
								className="[&_p.font-display]:text-red-500"
							/>
							<KpiStatCard
								label="Pending"
								value={analytics.pendingCount.toLocaleString()}
								iconClass="ri-time-line"
								accentClass="text-warning"
								className="[&_p.font-display]:text-amber-500"
							/>
							<KpiStatCard
								label="Revenue (succeeded)"
								value={formatMoney(analytics.succeededAmount, analytics.currency)}
								iconClass="ri-money-dollar-circle-line"
								accentClass="text-[#1ed27d]"
							/>
						</div>
					)}

					<PageFilter>
						<PageFilterFields className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 items-end">
							<div>
								<label className="form-label text-xs">Status</label>
								<select
									className="form-select w-full"
									value={filterStatus === '' ? '' : String(filterStatus)}
									onChange={(e) =>
										setFilterStatus(e.target.value === '' ? '' : (Number(e.target.value) as SubscriptionPaymentStatusTypes))
									}
								>
									<option value="">All statuses</option>
									<option value={SubscriptionPaymentStatusTypes.Succeeded}>Succeeded</option>
									<option value={SubscriptionPaymentStatusTypes.Failed}>Failed</option>
									<option value={SubscriptionPaymentStatusTypes.Pending}>Pending</option>
									<option value={SubscriptionPaymentStatusTypes.Refunded}>Refunded</option>
								</select>
							</div>
							<div>
								<label className="form-label text-xs">From date</label>
								<input type="date" className="form-input w-full" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
							</div>
							<div>
								<label className="form-label text-xs">To date</label>
								<input type="date" className="form-input w-full" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
							</div>
							<div className="xl:col-span-2">
								<label className="form-label text-xs">Search</label>
								<DebouncedSearchBar
									value={searchText}
									name="transactionSearch"
									id="transactionSearch"
									placeholder="Tenant, email, plan, Stripe payment id…"
									onChange={handleSearchChange}
									minSearchLength={1}
								/>
							</div>
						</PageFilterFields>
						<div className="flex flex-wrap gap-2 mt-3">
							<button type="button" className="btn btn-primary btn-sm" onClick={onApplyFilters}>
								Apply filters
							</button>
							<button type="button" className="btn btn-secondary btn-sm" onClick={onClearFilters}>
								Clear
							</button>
						</div>
					</PageFilter>

					{loading && !rowData && <AnimationSkeleton />}
					{rowData && (
						<div className={loading ? 'opacity-75' : ''}>
							<DataGridWithPagination
								rowData={rowData}
								columnDefs={columnDefs}
								onSortChanged={(event) => setGridSortingDetails(gridHelper.handleSortChanged(event))}
								onPageChange={async (page, pageSize) => {
									const nextPageSize = pageSize ?? selectedPageSize
									setSelectedPageSize(nextPageSize)
									await fetchTransactions(page, nextPageSize)
								}}
								rowHeight={56}
							/>
						</div>
					)}
				</PageBody>
			</PageWrapper>
			{(detailLoading || detail) && (
				<TransactionDetailModal
					detail={detail}
					loading={detailLoading}
					showTenant
					onClose={() => {
						setDetail(null)
						setDetailLoading(false)
					}}
				/>
			)}
		</>
	)
}

export default ManageTransactions
